import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaRegStar,
  FaStar,
  FaTimesCircle,
  FaUndoAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import PriceTooltip from "./ToolTip";
import ReactQuill from "react-quill";
import { formattedAmount } from "../../utils/helpers/Functions.helper";
import { PackageApi } from "../../apis/ProductListing.apis";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";

const ProductInfo = ({
  productData,
  totalRating,
  theme,
  selectedVariant,
  setSelectedColor,
}) => {
  const {
    stock,
    sales_price,
    mrp_price,
    harvest_date,
    package_type,
    nutritionalFields,
    dynamicFields,
    quality_grade,
    return_policy,
    replacement_policy,
    cancellation_policy,
  } = productData;

  const [packageData, setPackageData] = useState();
  const { product_details } = selectedVariant ? selectedVariant : productData;
  const [selectedSizeDetails, setSelectedSizeDetails] = useState(
    product_details && product_details[0]
  );

  const costPrice = mrp_price?.$numberDecimal;
  const salePrice = sales_price?.$numberDecimal;
  const grandTotal = salePrice;
  const discountAmount = costPrice - grandTotal;
  const percentageOff = (discountAmount / costPrice) * 100;

  // Set default color if none is selected
  useEffect(() => {
    if (!selectedVariant && productData?.product_color?.length > 0) {
      setSelectedColor(productData.product_color[0]);
    } else if (selectedVariant?.product_color?.length > 0) {
      setSelectedColor(selectedVariant.product_color[0]);
    }
  }, [productData, selectedVariant]);

  const getPackageData = async () => {
    try {
      const res = await PackageApi.getAllPackageMaster();
      const packagingData =
        res.data?.data.length &&
        res.data?.data.map((o, i) => {
          o.value = o._id;
          o.label = o?.packaging_name;
          return o;
        });

      setPackageData(packagingData);
    } catch (err) {
      Toast.error(err?.message);
    }
  };

  useEffect(() => {
    getPackageData();
  }, []);

  return (
    <div
      className={`p-4 ${
        theme === "light" ? "bg-white" : "bg-gray800"
      } rounded-lg shadow-sm`}
    >
      {/* Product Title */}
      <div className="mb-6">
        <h1
          className={`text-2xl md:text-3xl font-bold mb-2 ${
            theme === "light" ? "text-gray900" : "text-gray100"
          }`}
        >
          {selectedVariant
            ? selectedVariant.name
            : productData?.name || "Product Name"}
        </h1>

        {/* Ratings and Category */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div className="flex items-center mb-2 sm:mb-0">
            <div className="flex mr-3">
              {[...Array(5)].map((_, index) =>
                index < productData?.average_rating ? (
                  <FaStar key={index} className="text-yellow500 text-sm" />
                ) : (
                  <FaRegStar key={index} className="text-yellow500 text-sm" />
                )
              )}
              <span className="text-xs ml-1 text-gray500">
                ({productData?.average_rating?.toFixed(1) || 0})
              </span>
            </div>
            <span
              className={`text-xs ${
                theme === "light" ? "text-gray600" : "text-gray300"
              }`}
            >
              {totalRating?.toLocaleString("en-US") || 0} ratings
            </span>
          </div>

          <Tag
            value={productData?.sub_category?.name || "Sub Category"}
            className={`text-xs ${
              theme === "light"
                ? "bg-gray100 text-gray700"
                : "bg-gray700 text-gray200"
            }`}
          />
        </div>
      </div>

      {/* Price Section */}
      <div className="mb-6 p-4 rounded-lg bg-gray50 border border-gray200">
        <div className="flex items-baseline mb-1">
          {costPrice && (
            <span
              className={`line-through text-sm ${
                theme === "light" ? "text-gray500" : "text-gray400"
              }`}
            >
              {formattedAmount(costPrice)}
            </span>
          )}
          <div className="flex items-center ml-2">
            <span
              className={`text-2xl md:text-3xl font-bold ${
                theme === "light" ? "text-gray900" : "text-gray100"
              }`}
            >
              {formattedAmount(salePrice)}
            </span>
            {percentageOff > 0 && (
              <span className="ml-3 px-2 py-1 bg-red100 text-red800 text-xs font-medium rounded">
                {percentageOff.toFixed(0)}% OFF
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center text-xs text-gray500 mt-1">
          {/* <FaInfoCircle className="mr-1" /> */}
           <PriceTooltip
          mrp={costPrice}
          discount={discountAmount}
          specialPrice={grandTotal}
        />
          <span>Inclusive of all taxes</span>
        </div>

       
      </div>

      <div className="mb-6">
  <div className="flex items-center gap-3 flex-wrap">
    <div className="flex items-center gap-2">
      <span
        className={`font-medium ${
          theme === "light" ? "text-gray-700" : "text-gray-300"
        }`}
      >
        Availability:
      </span>
      <span
        className={`font-semibold flex items-center gap-1.5 ${
          stock > 0 ? "text-green600" : "text-red600"
        }`}
      >
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-current"></span>
        {stock > 0 ? "In Stock" : "Out of Stock"}
      </span>
    </div>

    {stock > 0 && stock < 10 && (
      <div className="text-sm text-yellow-800 bg-yellow-50 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap">
        <FaInfoCircle className="text-yellow-600" />
        Only <strong>{stock}</strong> left
      </div>
    )}
  </div>
</div>

      {/* Product Attributes */}
      <div className="mb-6">
        <h2
          className={`text-lg font-bold mb-3 pb-2 border-b ${
            theme === "light"
              ? "text-gray900 border-gray200"
              : "text-gray100 border-gray700"
          }`}
        >
          Product Details
        </h2>

        <div className="space-y-3">
          {harvest_date && (
            <div className="flex">
              <div
                className={`w-1/3 font-medium ${
                  theme === "light" ? "text-gray700" : "text-gray300"
                }`}
              >
                Harvest Date:
              </div>
              <div
                className={
                  theme === "light" ? "text-gray900" : "text-gray200"
                }
              >
                {new Date(harvest_date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          )}

          {quality_grade && (
            <div className="flex">
              <div
                className={`w-1/3 font-medium ${
                  theme === "light" ? "text-gray700" : "text-gray300"
                }`}
              >
                Quality Grade:
              </div>
              <div
                className={
                  theme === "light" ? "text-gray900" : "text-gray200"
                }
              >
                {quality_grade.replace(
                  /(\D*)(\d+)/,
                  (_, text, number) =>
                    `${text.charAt(0).toUpperCase()}${text.slice(1)} ${number}`
                )}
              </div>
            </div>
          )}

          {package_type && (
            <div className="flex">
              <div
                className={`w-1/3 font-medium ${
                  theme === "light" ? "text-gray700" : "text-gray300"
                }`}
              >
                Package Type:
              </div>
              <div
                className={
                  theme === "light" ? "text-gray900" : "text-gray200"
                }
              >
                {packageData?.find(
                  (item) => item.value === productData.package_type
                )?.packaging_name || "N/A"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Nutritional Information */}
      {(nutritionalFields || dynamicFields) && (
        <div className="mb-6">
          <h2
            className={`text-lg font-bold mb-3 pb-2 border-b ${
              theme === "light"
                ? "text-gray900 border-gray200"
                : "text-gray100 border-gray700"
            }`}
          >
            Nutritional Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nutritionalFields &&
              Object.entries(nutritionalFields).map(([key, value]) => (
                <div key={key} className="flex">
                  <div
                    className={`w-1/2 font-medium ${
                      theme === "light" ? "text-gray700" : "text-gray300"
                    }`}
                  >
                    {key.replace(/_/g, " ")}:
                  </div>
                  <div
                    className={
                      theme === "light" ? "text-gray900" : "text-gray200"
                    }
                  >
                    {value}
                  </div>
                </div>
              ))}

            {dynamicFields &&
              Object.entries(dynamicFields).map(([key, value]) => (
                <div key={key} className="flex">
                  <div
                    className={`w-1/2 font-medium ${
                      theme === "light" ? "text-gray700" : "text-gray300"
                    }`}
                  >
                    {key.replace(/_/g, " ")}:
                  </div>
                  <div
                    className={
                      theme === "light" ? "text-gray900" : "text-gray200"
                    }
                  >
                    {value}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Policies Section */}
      <div className="mb-4">
        <h2
          className={`text-lg font-bold mb-3 pb-2 border-b ${
            theme === "light"
              ? "text-gray900 border-gray200"
              : "text-gray100 border-gray700"
          }`}
        >
          Policies
        </h2>

        <div className="space-y-4">
          <PolicyItem
            icon={<FaUndoAlt className="text-yellow600 mr-2" />}
            label="Return Policy"
            policy={return_policy}
            theme={theme}
          />
          <PolicyItem
            icon={<FaCheckCircle className="text-green600 mr-2" />}
            label="Replacement Policy"
            policy={replacement_policy}
            theme={theme}
          />
          <PolicyItem
            icon={<FaTimesCircle className="text-red600 mr-2" />}
            label="Cancellation Policy"
            policy={cancellation_policy}
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
};

// Policy Display Component
const PolicyItem = ({ icon, label, policy, theme }) => (
  <div
    className={`p-3 rounded-lg ${
      theme === "light" ? "bg-gray50" : "bg-gray700"
    }`}
  >
    <div className="flex items-start">
      <div className="flex-shrink-0 pt-1">{icon}</div>
      <div className="ml-2">
        <h3
          className={`font-semibold ${
            theme === "light" ? "text-gray800" : "text-gray200"
          }`}
        >
          {label}
        </h3>
        {policy ? (
          <div
            className={`mt-1 text-sm ${
              theme === "light" ? "text-gray600" : "text-gray300"
            }`}
            dangerouslySetInnerHTML={{ __html: policy }}
          />
        ) : (
          <p
            className={`mt-1 text-sm italic ${
              theme === "light" ? "text-gray500" : "text-gray400"
            }`}
          >
            Not specified
          </p>
        )}
      </div>
    </div>
  </div>
);

export default ProductInfo;
